import { computed, effect, inject, Injectable, OnDestroy, signal, untracked } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { RoutePaths } from '../../app/app.routes';
import { Router } from '@angular/router';
import { UserService } from '../user/user-svc.ng';
import { Nullable } from '../../classes/nullable';
import { LoginInfo, AuthTokenRefreshResult, RevalidationInfo, RevalidationListenerInfo, TestConnectionInfo } from './auth';
import { SnackBarService } from '@polk_county_public_schools/ngx-pcps-dialogs/snackbar';
import { UserInfo } from '../user/user';
import { addSeconds, differenceInSeconds, isValid } from 'date-fns';
import { SessionStorage } from 'ngx-webstorage';
import { VERSION_NUMBER } from '../version/version.service';
import { ConfirmationDialogOptions, ConfirmationDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/confirmation';
import { MessageBoxDialogService } from '@polk_county_public_schools/ngx-pcps-dialogs/messagebox';
import { SplashService } from '@polk_county_public_schools/ngx-pcps-dialogs/splash';
import { ErrorService } from '../../services/error/error.service';
import { BehaviorSubject, map, Observable, of } from 'rxjs';
import { UnsavedChangesService } from '../../app/unsaved-changes-svc.ng';

@Injectable({
    providedIn: 'root',
})
export class AuthService implements OnDestroy {
    // #region Injects

    protected readonly confdialog = inject(ConfirmationDialogService);
    protected readonly errorSvc = inject(ErrorService);
    protected readonly http = inject(HttpClient);
    protected readonly msgBoxSvc = inject(MessageBoxDialogService);
    protected readonly router = inject(Router);
    protected readonly snackSvc = inject(SnackBarService);
    protected readonly splashService = inject(SplashService);
    protected readonly unsavedChangesSvc = inject(UnsavedChangesService);
    protected readonly userSvc = inject(UserService);

    // #endregion

    // #region Properties

    // 45 minutes (time period to wait between successive checks of whether the auth token is recently used)
    static AUTHCHECKTIME: number = 45 * 60;
    // 2 minutes, 50 seconds (time period to wait before logging the user out automatically). Roughly 20 minutes total between check
    // and autologout
    static LOGOUTWARNINGTIME = 170;

    @SessionStorage() nextAuthRefresh!: string | null;
    @SessionStorage() private systemOnline!: boolean;

    protected authTokenCancelFlag = false;
    protected authTokenRefreshHandle: number | null = null;
    protected authRefreshDeadline: Date | null = this.nextAuthRefresh ? new Date(this.nextAuthRefresh) : null;
    protected readonly hasVersionUpdate = signal(false);
    protected isRefreshing = false;
    readonly revalidationHistory: Map<string, BehaviorSubject<RevalidationListenerInfo | null>> = new Map();
    readonly systemOnlineStatus = signal<TestConnectionInfo>({
        SystemMessage: '',
        Status: this.systemOnline,
        JAuctionVersion: VERSION_NUMBER,
    });
    protected readonly user = computed(() => this.userSvc.user());
    protected readonly versionNumber = signal(VERSION_NUMBER);

    // #endregion

    constructor() {
        // Effect to ensure that auth token refreshing takes place when we get a user with an authtoken.
        effect(() => {
            const user = this.user();

            untracked(() => {
                if (user?.AuthToken && !this.isRefreshing) {
                    untracked(() => this.startAuthTokenRefreshing());
                }
            });
        });
    }

    ngOnDestroy(): void {
        this.cancelAuthTokenRefreshing();

        // Complete and tear down any of the revalidation BehaviorSubjects that might have been created during this session.
        for (let key in this.revalidationHistory.keys) {
            const item = this.revalidationHistory.get(key);
            if (item) {
                item.complete();
            }
        }

        this.revalidationHistory.clear();
    }

    // #region Auth Token Refresh Method

    protected authTokenRefresh(): void {
        // Now retrieve the auth token for the currently logged in user, stored in the userService
        const auth = this.user()?.AuthToken;

        // Send a request to the system to refresh the token and reset all countdown timers.
        this.refreshAuthToken().subscribe({
            next: (newAuthPacket) => {
                const newAuth = newAuthPacket?.AuthToken || '';

                if (newAuthPacket?.JAuctionVersion) {
                    // We have version information. Does it match the version known here in the client UI?
                    // If not, set the update flag.
                    if (VERSION_NUMBER !== newAuthPacket.JAuctionVersion) {
                        // Mismatch. Set the flag.
                        this.hasVersionUpdate.set(true);
                    }
                }

                // We have a valid, new auth token. Update the user variable in the userService to reflect this.
                this.userSvc.updateAuthToken(newAuth);

                // If there is no flag signal set preventing this loop from continuing, then we can set up the next iteration with a timeout now.
                if (!this.authTokenCancelFlag) {
                    this.authTokenRefreshHandle = window.setTimeout(() => {
                        this.authTokenRefresh();
                    }, AuthService.AUTHCHECKTIME * 1000);

                    // Set the refresh deadline app value.
                    const Now = new Date();
                    this.authRefreshDeadline = addSeconds(Now, AuthService.AUTHCHECKTIME);
                    this.nextAuthRefresh = this.authRefreshDeadline?.toISOString() || '';
                }
            },
            error: (err) => {
                // An error occurred. Attempt to retrieve it.
                const errMsg =
                    this.errorSvc.retrieveServiceErrorMessage(err) ||
                    'An error occurred while trying to validate your active connection to the system. You have been logged out.';

                this.snackSvc.error(errMsg, 5000);
            },
        });
    }

    // #endregion

    // #region Cancel Auth Token Refreshing Method

    cancelAuthTokenRefreshing(): void {
        // If possible, use the handle to cancel the timeout from happening
        if (this.authTokenRefreshHandle != null) {
            window.clearTimeout(this.authTokenRefreshHandle);
        }

        // Cancel the flag, handle, and deadline to refresh
        this.authTokenRefreshHandle = null;
        this.authTokenCancelFlag = false;
        this.authRefreshDeadline = null;
        this.isRefreshing = false;
        this.nextAuthRefresh = null;
    }

    // #endregion

    // #region Create Account

    createAccount(firstName: Nullable<string>, lastName: Nullable<string>, email: Nullable<string>, password: Nullable<string>) {
        return this.http.post<boolean>(`api/Login/create`, {
            FirstName: firstName || '',
            LastName: lastName || '',
            Email: email || '',
            Password: password || '',
        });
    }

    // #endregion

    // #region Logout

    logout(confirmation?: boolean, reason?: number) {
        let confObs: Observable<boolean | null>;

        if (confirmation === true) {
            // Pop up a confirmation box to confirm desire to logout.
            const opts: ConfirmationDialogOptions = {
                TitleText: 'Confirmation Requested',
                MessageText: 'You are about to log out of the site. Are you sure you wish to exit at this time?',
                OKText: 'Yes - Log Out',
                CancelText: 'No - Stay Logged In',
            };

            confObs = this.confdialog.Open(opts);
        } else {
            // No dialog needed.
            confObs = of<boolean>(true);
        }

        confObs.subscribe((result) => {
            if (!result) return;

            // User is opting to log out. Proceed.
            this.logoutFromServer().subscribe();

            // Stop authtoken refreshing.
            this.cancelAuthTokenRefreshing();

            // Clear the user.
            this.userSvc.clearUser();

            // Snack success if this was user initiated logout (reason null or 0)
            if (reason == null || reason === 0) {
                this.snackSvc.success('Logged out successfully.');
            }

            // Clear out change tracking.
            this.unsavedChangesSvc.clearTracking();

            // Redirect to the login page.
            this.router.navigate([`/${RoutePaths.Home}`]);
        });
    }

    // #endregion

    // #region Logout From Server

    logoutFromServer(reason?: Nullable<number>) {
        return this.http.delete<boolean>(`api/Login${reason != null ? '?Reason=' + reason : ''}`);
    }

    // #endregion

    // #region Refresh Authentication Token Service Method

    refreshAuthToken() {
        return this.http.put<AuthTokenRefreshResult>(`api/Login`, {});
    }

    // #endregion

    // #region Revalidate Session Service Call

    revalidateSession(Username: string, Password: string, ImpersonatedUsername: string) {
        return this.http
            .put<UserInfo>(`api/Login/revalidate`, {
                Username: Username || '',
                Password: Password || '',
                ImpersonatedUsername: ImpersonatedUsername || '',
            })
            .pipe(
                map((data) => {
                    return {
                        Error: null,
                        UserInfo: data,
                    } as RevalidationInfo;
                })
            );
    }

    // #endregion

    // #region Set System Online Status

    setSystemOnlineStatus(status: TestConnectionInfo): void {
        this.systemOnline = status?.Status || false;
        this.systemOnlineStatus.set(status);
    }

    // #endregion

    // #region Start AuthToken Refreshing Service Call

    startAuthTokenRefreshing(): void {
        // This process will work as follows: We will call a service method which will tell us if the current user's auth token shows
        // "recent use" (which is a flag set by most stored procedures). If that method succeeds, and shows recent use, we will simply
        // use a different service call to update the auth token refresh time, then delay for a time period (AUTHCHECKTIME) before we
        // run this same check again.
        // If at any point the recent use flag is not set, then we set a timer to logout the user after a set amount of time
        // (LOGOUTWARNINGTIME), and simultaneously put up a dialog box that gives the user a visual representation of the countdown,
        // along with a button they can click to "save" their session, which essentially manually triggers the refresh method.

        // Initiate the auth token refresh cycling by calling the refresh method the first time
        this.authTokenCancelFlag = false;

        // Check real quick for a present value for AuthRefreshDeadline. If that exists, then it indicates the time when we absolutely
        // need to refresh the auth token, so we should set the refresh handle time to just before then. Otherwise, this is probably a
        // first login so use the default time.
        let timeToRefresh = AuthService.AUTHCHECKTIME;

        if (this.nextAuthRefresh) {
            // Read the deadline time to refresh from the session storage, convert into a Date object.
            this.authRefreshDeadline = new Date(this.nextAuthRefresh);
        } else {
            // Nothing available from session storage, so queue up a fresh refresh clock.
            this.authRefreshDeadline = addSeconds(new Date(), AuthService.AUTHCHECKTIME);
        }

        if (this.authRefreshDeadline && isValid(this.authRefreshDeadline)) {
            // Find the time difference in seconds between now and when we are due to refresh.
            const timediff = differenceInSeconds(this.authRefreshDeadline, new Date());

            // We will always use the lesser of the next known time to refresh and the usual delay time. This is in case we are refreshing
            // the browser and need to restart refresh cycle, as opposed to fresh starting cycles.
            if (timediff < timeToRefresh) {
                timeToRefresh = timediff;
            }
            if (timeToRefresh <= 0) {
                timeToRefresh = 3;
            } // Ensure at least a 3 second wait.
        }

        this.authTokenRefreshHandle = window.setTimeout(() => {
            this.authTokenRefresh();
        }, timeToRefresh * 1000);

        // Set the refresh deadline app value
        const Now = new Date();

        this.authRefreshDeadline = addSeconds(Now, timeToRefresh);
        this.isRefreshing = true;
        this.nextAuthRefresh = this.authRefreshDeadline?.toISOString() || '';
    }

    // #endregion

    // #region Test Connection Service Call

    testConnection() {
        // Call the WebApi to see if anyone is home.
        return this.http.get<TestConnectionInfo>('api/Login/testconnection');
    }

    // #endregion

    // #region Validate User

    validateUser(email: Nullable<string>, password: Nullable<string>, screen: Nullable<string>, window: Nullable<string>) {
        return this.http.post<LoginInfo>(`api/Login`, {
            Email: email || '',
            Password: password || '',
            Screen: screen || '',
            Window: window || '',
        });
    }

    // #endregion

    // #region Verify Account

    verifyAccount(token: Nullable<string>) {
        return this.http.put<UserInfo>(`api/Login/verify`, {
            valToken: token || '',
        });
    }

    // #endregion
}
